import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TopBar } from "@/components/layout/TopBar";
import { AIThinkingState } from "@/components/AltioraComponents";
import { toast } from "sonner";
import {
  Flame, Sparkles, ChevronRight, X, Wand2, ArrowUpRight, ShieldCheck,
  Users, MapPin, TrendingUp, Info, XCircle, CheckCircle2, Circle, Users2,
} from "lucide-react";

const BAND_STYLES = {
  critical: { border: "border-[#DC2626]/35", bg: "bg-[#FEF2F2]", ring: "bg-[#DC2626]", text: "text-[#991B1B]", chip: "text-[#DC2626] bg-[#FEE2E2]" },
  high:     { border: "border-[#F59E0B]/45", bg: "bg-[#FEF7EC]", ring: "bg-[#F59E0B]", text: "text-[#B45309]", chip: "text-[#B45309] bg-[#FEF3C7]" },
  followup: { border: "border-[#C5A880]/45", bg: "bg-[#FBF6EA]", ring: "bg-[#C5A880]", text: "text-[#7A5B14]", chip: "text-[#7A5B14] bg-[#FBF6EA]" },
  info:     { border: "border-[#059669]/35", bg: "bg-[#ECFDF5]", ring: "bg-[#059669]", text: "text-[#065F46]", chip: "text-[#065F46] bg-[#D1FAE5]" },
};

const SOURCE_ICON = {
  score_dimension: TrendingUp,
  gps_opportunity: MapPin,
  professional_match: Users,
};

export default function Priorities() {
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all/critical/high/followup/info
  const [expanded, setExpanded] = useState(null);
  const [narratives, setNarratives] = useState({}); // rec_key -> narrative
  const [narrating, setNarrating] = useState({});
  const [dismissing, setDismissing] = useState({});
  const [cohortData, setCohortData] = useState({}); // rec_key -> cohort payload

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/recommendations/priority");
      setData(r.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const humanize = async (rec_key) => {
    setNarrating((n) => ({ ...n, [rec_key]: true }));
    try {
      const { data: h } = await api.post(`/recommendations/${rec_key}/humanize`, {});
      setNarratives((prev) => ({ ...prev, [rec_key]: h.narrative }));
    } catch {
      toast.error(lang === "fr" ? "L'humanisation a échoué." : "Humanization failed.");
    } finally {
      setNarrating((n) => ({ ...n, [rec_key]: false }));
    }
  };

  const dismiss = async (rec_key) => {
    setDismissing((d) => ({ ...d, [rec_key]: true }));
    try {
      await api.post(`/recommendations/${rec_key}/dismiss`, {});
      toast.success(lang === "fr" ? "Recommandation masquée." : "Recommendation dismissed.");
      await refresh();
    } catch {
      toast.error(lang === "fr" ? "Impossible de masquer — réessayez." : "Failed to dismiss — please retry.");
    } finally {
      setDismissing((d) => ({ ...d, [rec_key]: false }));
    }
  };

  const trackAct = async (rec_key) => {
    api.post(`/recommendations/${rec_key}/act`, {}).catch(() => {});
    // Also log the 'planned' impact lifecycle event
    api.post(`/impact/track/${rec_key}`, { event_type: "planned" }).catch(() => {});
  };

  const trackLifecycle = async (rec_key, event_type) => {
    try {
      await api.post(`/impact/track/${rec_key}`, { event_type });
      toast.success(lang === "fr" ? "Impact enregistré." : "Impact recorded.");
    } catch {
      toast.error(lang === "fr" ? "Échec de l'enregistrement." : "Failed to record.");
    }
  };

  const loadCohort = async (rec_key) => {
    try {
      const { data } = await api.get(`/impact/cohort/${rec_key}`);
      setCohortData((c) => ({ ...c, [rec_key]: data }));
    } catch { /* silent */ }
  };

  const items = (data?.items || []).filter((r) => filter === "all" || r.band === filter);
  const counts = data?.counts || { critical: 0, high: 0, followup: 0, info: 0 };

  return (
    <div className="min-h-full" data-testid="priorities-root">
      <TopBar
        title={lang === "fr" ? "Actions prioritaires" : "Priority actions"}
        subtitle={lang === "fr"
          ? "Le moteur d'orchestration ALTIORA classe vos actions par impact sur votre trajectoire économique."
          : "The ALTIORA orchestration engine ranks your actions by economic trajectory impact."}
      />

      <div className="px-6 md:px-10 py-6 max-w-6xl mx-auto space-y-6">
        {/* Band filters — priority queue tabs */}
        <div className="flex flex-wrap gap-2" data-testid="priorities-filters">
          <FilterChip label={lang === "fr" ? `Tous (${(data?.items || []).length})` : `All (${(data?.items || []).length})`}
                      active={filter === "all"} onClick={() => setFilter("all")} testId="filter-all" />
          <FilterChip emoji="🔴" label={`${counts.critical} ${lang === "fr" ? "critique" : "critical"}`}
                      active={filter === "critical"} onClick={() => setFilter("critical")} testId="filter-critical" />
          <FilterChip emoji="🟠" label={`${counts.high} ${lang === "fr" ? "élevée" : "high"}`}
                      active={filter === "high"} onClick={() => setFilter("high")} testId="filter-high" />
          <FilterChip emoji="🟡" label={`${counts.followup} ${lang === "fr" ? "suivi" : "follow-up"}`}
                      active={filter === "followup"} onClick={() => setFilter("followup")} testId="filter-followup" />
          <FilterChip emoji="🟢" label={`${counts.info} ${lang === "fr" ? "info" : "info"}`}
                      active={filter === "info"} onClick={() => setFilter("info")} testId="filter-info" />
        </div>

        {loading ? (
          <div className="rounded-xl border border-[#E4E1D8] bg-white p-8 shadow-sm">
            <AIThinkingState lang={lang} />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-[#E4E1D8] bg-white p-12 text-center shadow-sm">
            <Sparkles className="h-8 w-8 text-[#C5A880] mx-auto" strokeWidth={1.5} />
            <div className="mt-4 font-display text-lg text-[#1F2937]">
              {lang === "fr" ? "Aucune action à cette priorité" : "No action at this priority"}
            </div>
            <div className="mt-2 text-sm text-[#5B5B62]">
              {lang === "fr" ? "Élargissez la sélection ou complétez votre diagnostic." : "Widen the selection or complete your diagnostic."}
            </div>
          </div>
        ) : (
          <div className="space-y-3 altiora-stagger">
            {items.map((rec) => {
              const s = BAND_STYLES[rec.band] || BAND_STYLES.info;
              const Ico = SOURCE_ICON[rec.source] || Sparkles;
              const isExpanded = expanded === rec.rec_key;
              const narrative = narratives[rec.rec_key];
              return (
                <div
                  key={rec.rec_key}
                  data-testid={`priority-item-${rec.rec_key}`}
                  className={`relative rounded-xl border ${s.border} ${s.bg} shadow-sm altiora-transition`}
                >
                  <div className={`absolute left-0 top-4 bottom-4 w-1 rounded-r-full ${s.ring}`}></div>
                  <div className="pl-5 pr-5 py-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest ${s.chip}`}>
                            <span>{rec.emoji}</span> {rec.label}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full border border-[#E4E1D8] bg-white/70 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
                            <Ico className="h-3 w-3" strokeWidth={2} />
                            {rec.source.replace(/_/g, " ")}
                          </span>
                          <span className="font-mono text-[10px] uppercase tracking-widest text-[#5B5B62]">
                            {lang === "fr" ? "Impact" : "Impact"} <span className="text-[#1F2937] font-medium">{Math.round(rec.impact_score)}</span>
                          </span>
                          <span className="font-mono text-[10px] uppercase tracking-widest text-[#5B5B62]">
                            {lang === "fr" ? "Horizon" : "Horizon"} <span className="text-[#1F2937] font-medium">{rec.time_horizon_days}j</span>
                          </span>
                        </div>
                        <div className="mt-2 font-display text-lg text-[#1F2937] tracking-tight">
                          {rec.title}
                        </div>
                        <div className="mt-1.5 text-sm text-[#5B5B62] leading-relaxed">
                          {rec.raw_message}
                        </div>

                        {/* Humanized narrative */}
                        {narrative && (
                          <div className="mt-4 rounded-lg border border-[#0B2545]/15 bg-white p-4 shadow-inner">
                            <div className="flex items-center gap-2 mb-2">
                              <Wand2 className="h-3.5 w-3.5 text-[#0B2545]" strokeWidth={2} />
                              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545]">
                                {lang === "fr" ? "ALTIORA vous parle" : "ALTIORA speaks"}
                              </div>
                            </div>
                            <div className="text-sm text-[#1F2937] leading-relaxed whitespace-pre-line">
                              {narrative}
                            </div>
                          </div>
                        )}

                        {isExpanded && (
                          <div className="mt-4 rounded-lg border border-[#E4E1D8] bg-white/70 p-4">
                            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545] mb-2">
                              Trusted Intelligence™
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <TrustRow label={lang === "fr" ? "Gap" : "Gap"} value={rec.breakdown?.gap_severity} />
                              <TrustRow label={lang === "fr" ? "Urgence" : "Urgency"} value={rec.breakdown?.time_pressure} />
                              <TrustRow label={lang === "fr" ? "Impact éco." : "Econ. impact"} value={rec.breakdown?.economic_impact} />
                              <TrustRow label={lang === "fr" ? "Irréversibilité" : "Irreversibility"} value={rec.breakdown?.irreversibility} />
                              <TrustRow label={lang === "fr" ? "Effort" : "Effort"} value={`×${rec.breakdown?.effort_multiplier}`} />
                              <TrustRow label={lang === "fr" ? "Confiance" : "Confidence"} value={`${Math.round((rec.trust?.confidence || 0) * 100)}%`} />
                            </div>
                            <div className="mt-3 flex items-start gap-2 text-[11px] text-[#5B5B62]">
                              <ShieldCheck className="h-3 w-3 mt-0.5 text-[#059669] flex-shrink-0" strokeWidth={2} />
                              <span>{rec.trust?.why}</span>
                            </div>
                            <div className="mt-2 text-[11px] font-mono uppercase tracking-widest text-[#8B8B8F]">
                              {lang === "fr" ? "Aide à la décision uniquement — jamais une garantie." : "Decision support only — never a guarantee."}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <Link
                        to={rec.deep_link || "/app"}
                        onClick={() => trackAct(rec.rec_key)}
                        data-testid={`priority-act-${rec.rec_key}`}
                        className="inline-flex items-center gap-1.5 bg-[#0B2545] hover:bg-[#123163] text-white text-xs font-medium px-4 py-2 rounded-md altiora-transition shadow-sm"
                      >
                        {lang === "fr" ? "Passer à l'action" : "Take action"}
                        <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
                      </Link>
                      <button
                        onClick={() => humanize(rec.rec_key)}
                        disabled={!!narrating[rec.rec_key]}
                        data-testid={`priority-humanize-${rec.rec_key}`}
                        className="inline-flex items-center gap-1.5 border border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white text-xs font-medium px-3.5 py-2 rounded-md altiora-transition disabled:opacity-60"
                      >
                        <Wand2 className="h-3.5 w-3.5" strokeWidth={2} />
                        {narrating[rec.rec_key]
                          ? (lang === "fr" ? "ALTIORA rédige…" : "ALTIORA writing…")
                          : narrative
                            ? (lang === "fr" ? "Reformuler" : "Rephrase")
                            : (lang === "fr" ? "ALTIORA me parle" : "Have ALTIORA speak")}
                      </button>
                      <button
                        onClick={() => loadCohort(rec.rec_key)}
                        data-testid={`priority-cohort-${rec.rec_key}`}
                        className="inline-flex items-center gap-1.5 text-[#5B5B62] hover:text-[#0B2545] text-xs font-medium px-3 py-2 rounded-md altiora-transition"
                      >
                        <Users2 className="h-3.5 w-3.5" strokeWidth={2} />
                        {lang === "fr" ? "Personnes similaires" : "People like you"}
                      </button>
                      <button
                        onClick={() => setExpanded(isExpanded ? null : rec.rec_key)}
                        className="inline-flex items-center gap-1.5 text-[#5B5B62] hover:text-[#1F2937] text-xs font-medium px-3 py-2 rounded-md altiora-transition"
                      >
                        <Info className="h-3.5 w-3.5" strokeWidth={2} />
                        {isExpanded ? (lang === "fr" ? "Masquer" : "Hide") : "Trusted Intelligence™"}
                      </button>
                      <button
                        onClick={() => dismiss(rec.rec_key)}
                        disabled={!!dismissing[rec.rec_key]}
                        data-testid={`priority-dismiss-${rec.rec_key}`}
                        className="ml-auto inline-flex items-center gap-1 text-[#8B8B8F] hover:text-[#DC2626] text-xs font-mono uppercase tracking-widest px-2 py-1 altiora-transition"
                      >
                        <XCircle className="h-3.5 w-3.5" strokeWidth={2} />
                        {lang === "fr" ? "Masquer" : "Dismiss"}
                      </button>
                    </div>

                    {/* Cohort insight */}
                    {cohortData[rec.rec_key] && (
                      <div className="mt-3 rounded-lg border border-[#0B2545]/20 bg-white p-3 flex items-start gap-2">
                        <Users2 className="h-4 w-4 mt-0.5 text-[#0B2545] flex-shrink-0" strokeWidth={2} />
                        <div className="text-xs text-[#1F2937] leading-relaxed">
                          {cohortData[rec.rec_key][`narrative_${lang}`]
                            || cohortData[rec.rec_key][`note_${lang}`]
                            || (lang === "fr" ? "Cohort en cours de constitution." : "Cohort being built.")}
                        </div>
                      </div>
                    )}

                    {/* Impact tracking (post-action) */}
                    <div className="mt-3 pt-3 border-t border-[#E4E1D8]/60 flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">
                        {lang === "fr" ? "Où en êtes-vous ?" : "Where are you?"}
                      </span>
                      <TrackBtn onClick={() => trackLifecycle(rec.rec_key, "attended")} testId={`track-attended-${rec.rec_key}`}>
                        <CheckCircle2 className="h-3 w-3" strokeWidth={2} />
                        {lang === "fr" ? "J'ai agi" : "I acted"}
                      </TrackBtn>
                      <TrackBtn onClick={() => trackLifecycle(rec.rec_key, "outcome_positive")} testId={`track-positive-${rec.rec_key}`}>
                        <TrendingUp className="h-3 w-3 text-[#059669]" strokeWidth={2} />
                        {lang === "fr" ? "Résultat positif" : "Positive outcome"}
                      </TrackBtn>
                      <TrackBtn onClick={() => trackLifecycle(rec.rec_key, "outcome_neutral")} testId={`track-neutral-${rec.rec_key}`}>
                        <Circle className="h-3 w-3 text-[#8B8B8F]" strokeWidth={2} />
                        {lang === "fr" ? "Neutre" : "Neutral"}
                      </TrackBtn>
                      <TrackBtn onClick={() => trackLifecycle(rec.rec_key, "outcome_negative")} testId={`track-negative-${rec.rec_key}`}>
                        <XCircle className="h-3 w-3 text-[#DC2626]" strokeWidth={2} />
                        {lang === "fr" ? "Sans résultat" : "No outcome"}
                      </TrackBtn>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function FilterChip({ emoji, label, active, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-widest altiora-transition border ${
        active
          ? "bg-[#0B2545] border-[#0B2545] text-white"
          : "bg-white border-[#E4E1D8] text-[#5B5B62] hover:border-[#B8B5AC]"
      }`}
    >
      {emoji && <span className="text-sm leading-none">{emoji}</span>}
      {label}
    </button>
  );
}

function TrustRow({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-md bg-[#F8F7F3] border border-[#E4E1D8] px-2.5 py-1.5">
      <span className="font-mono text-[9px] uppercase tracking-widest text-[#8B8B8F]">{label}</span>
      <span className="text-xs text-[#1F2937] font-medium">{value ?? "—"}</span>
    </div>
  );
}

function TrackBtn({ children, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] hover:text-[#0B2545] px-2 py-1 rounded border border-[#E4E1D8] bg-white hover:border-[#0B2545] altiora-transition"
    >
      {children}
    </button>
  );
}
